import React from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../../../hooks/use-media-query';
import { RootState } from '../../../../reducers';
import supportedLanguages, { LanguageCodeType } from './supportedLanguages';
import { updateSettings } from '../../../settings/settingsSlice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../../components/ui/dropdown-menu';
import { Button } from '../../../../components/ui/button';

interface LanguageSelectProps {
  noCollapse?: boolean;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({ noCollapse }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isSmallOrMedium = useMediaQuery('(max-width: 967px)'); // md breakpoint
  const recorder = useSelector((state: RootState) => state.settings.recorder);
  const defaultTranscriptionLanguage =
    recorder.otherSettings?.defaultTranscriptionLanguage || undefined;

  const handleUpdateLanguageSettings = (language: LanguageCodeType) => {
    dispatch(
      updateSettings({
        key: 'recorder',
        newSettings: {
          ...recorder,
          otherSettings: {
            ...recorder.otherSettings,
            defaultTranscriptionLanguage: language,
          },
        },
      }),
    );
  };

  const topLanguages = supportedLanguages.slice(0, 8);
  const otherLanguages = supportedLanguages.slice(8);

  const selectedLanguage =
    supportedLanguages.find(
      (lang) => lang.value === defaultTranscriptionLanguage,
    ) || supportedLanguages[0]; // Default to 'Auto' if not found

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {isSmallOrMedium && !noCollapse ? (
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8"
            title={t('library.empty.language.tooltip')}
          >
            <Globe className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="ghost" className="w-full justify-between">
            <span>{selectedLanguage.label}</span>
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px] max-w-[160px]">
        {topLanguages.map((option) => {
          const isSelected = option.value === defaultTranscriptionLanguage;
          return (
            <DropdownMenuItem
              key={option.value || 'auto'}
              onClick={() => handleUpdateLanguageSettings(option.value)}
            >
              <span className="flex-1 truncate" title={option.label}>
                {option.label}
              </span>
              {isSelected && (
                <div className="w-2 h-2 bg-primary rounded-full ml-2" />
              )}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        {otherLanguages.map((option) => {
          const isSelected = option.value === defaultTranscriptionLanguage;
          return (
            <DropdownMenuItem
              key={option.value || 'auto'}
              onClick={() => handleUpdateLanguageSettings(option.value)}
            >
              <span className="flex-1 truncate" title={option.label}>
                {option.label}
              </span>
              {isSelected && (
                <div className="w-2 h-2 bg-primary rounded-full ml-2" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelect;
